
import React, { useState, useCallback, useEffect, useMemo } from "react";
import DriverScheduleTable from "./Partes/DriverScheduleTable";
import FilterComponent from "./Partes/FilterComponent";
import { formateoBasico } from "../../../utilities/formateos";
import { getServices } from "./MaestroDeServiciosIntegracion";
import ScrollToTopButton from "../../../components/atoms/ScrollToTopButton";
import Swal from "sweetalert2";

// Memoización de los componentes hijos para evitar re-renders innecesarios
const MemoizedFilterComponent = React.memo(FilterComponent);
const MemoizedDriverScheduleTable = React.memo(DriverScheduleTable);

const MaestroDeServicios = () => {
    // Definición de estados
    const [fecha, setFecha] = useState(new Date());
    const [cliente, setCliente] = useState(null);
    const [proveedor, setProveedor] = useState(null);
    const [ruta, setRuta] = useState(null);
    const [operador, setOperador] = useState(null);
    const [unidad, setUnidad] = useState(null);
    const [tipoUnidad, setTipoUnidad] = useState(null);
    const [driversData, setDriversData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFilterType, setSelectedFilterType] = useState('operador');
    const [kmCalculado, setKmCalculado] = useState(false);
    const [zona, setZona] = useState('maestro');
    const [selectedColumns, setSelectedColumns] = useState([
        { name: 'OPERADOR', code: 'OPERADOR' },
        { name: 'UNIDAD', code: 'UNIDAD' },
        { name: 'UNIDAD_TIPO', code: 'UNIDAD_TIPO' },
        { name: 'RUTA', code: 'RUTA' },
        { name: 'SERVICIOS', code: 'SERVICIOS' },
    ]);
    // Inicializamos params con un objeto para evitar el error de "null"
    const [params, setParams] = useState({
        fecha: formateoBasico(new Date()),
        cliente: null,
        proveedor: null,
        ruta: null,
        operador: null,
        unidad: null,
        tipoUnidad: null,
        selectedFilterType: 'operador',
        km_wialon: false
    });

    // Actualizamos los parámetros cada vez que un filtro cambia
    useEffect(() => {
        setParams({
            fecha: formateoBasico(fecha),
            cliente: cliente ? cliente.code : null,
            proveedor: proveedor ? proveedor.code : null,
            ruta: ruta ? ruta.code : null,
            operador: operador ? operador.code : null,
            unidad: unidad ? unidad : null,
            tipoUnidad: tipoUnidad ? tipoUnidad.code : null,
            selectedFilterType: selectedFilterType,
            km_wialon: kmCalculado
        });
    }, [fecha, cliente, proveedor, ruta, operador, unidad, tipoUnidad, selectedFilterType,kmCalculado]);
    useEffect(() => {
    //    SWALL FIRE PARA ALERTA DE RELENTIZACION CAUSADA POR QUE SE CALCULA EL KILOMETRAJE
        if(kmCalculado){
            Swal.fire({
                icon: 'info',
                title: 'KM Calculado',
                text: 'El cálculo de KM está activado. Esto puede causar una ralentización.'
            });
            setSelectedColumns(prevColumns => {
                // Verificamos si la columna ya está incluida
                const isKmCalculadoIncluded = prevColumns.some(col => col.code === 'KM_CALCULADO');
                if (!isKmCalculadoIncluded) {
                    return [...prevColumns, { name: 'KM CALCULADO', code: 'KM_CALCULADO' }];
                }
                return prevColumns;
            });
        }
        else{
            setSelectedColumns(prevColumns => prevColumns.filter(col => col.code !== 'KM_CALCULADO'));
        }
    }, [kmCalculado]);
    // Memoizamos la función de consulta de datos para que no se recree en cada render
    // Es clave para que el componente hijo la reciba como una prop estable
    const fetchDriversData = useCallback(async (currentParams) => {
        if (!currentParams) return;
        setLoading(true);
        try {
            await getServices(setDriversData, currentParams);
        } catch (error) {
            console.error("Error fetching driver data:", error);
            setDriversData([]);
        } finally {
            setLoading(false);
        }
    }, [kmCalculado]);

    // Esta función maneja la lógica de "Consultar", construyendo los parámetros
    // y activando la llamada a la API en un solo lugar.
    const handleConsultar = useCallback(() => {
        const newParams = {
            fecha: formateoBasico(fecha),
            cliente: cliente ? cliente.code : null,
            proveedor: proveedor ? proveedor.code : null,
            ruta: ruta ? ruta.code : null,
            operador: operador ? operador.code : null,
            unidad: unidad ? unidad : null,
            tipoUnidad: tipoUnidad ? tipoUnidad.code : null,
            selectedFilterType: selectedFilterType,
            km_wialon: kmCalculado
        };
        // Actualizamos el estado de params, lo que activará la consulta en el componente hijo.
        fetchDriversData(newParams);
    }, [fecha, cliente, proveedor, ruta, operador, unidad, tipoUnidad, selectedFilterType, fetchDriversData, kmCalculado]);

    const clear = useCallback(() => {
        setFecha(new Date());
        setCliente(null);
        setProveedor(null);
        setRuta(null);
        setOperador(null);
        setUnidad(null);
        setTipoUnidad(null);
        setDriversData([]);
        setLoading(false);
    }, []);

    return (
        <>
            <div className="scroll">
                <ScrollToTopButton />
                <MemoizedFilterComponent
                    fecha={fecha}
                    setFecha={setFecha}
                    cliente={cliente}
                    setCliente={setCliente}
                    proveedor={proveedor}
                    setProveedor={setProveedor}
                    ruta={ruta}
                    setRuta={setRuta}
                    operador={operador}
                    setOperador={setOperador}
                    unidad={unidad}
                    setUnidad={setUnidad}
                    tipoUnidad={tipoUnidad}
                    setTipoUnidad={setTipoUnidad}
                    selectedFilterType={selectedFilterType}
                    setSelectedFilterType={setSelectedFilterType}
                    zona={zona}
                    setZona={setZona}
                    selectedColumns={selectedColumns}
                    setSelectedColumns={setSelectedColumns}
                    handleConsultar={handleConsultar}
                    clear={clear}
                    setKmCalculado={setKmCalculado}
                    kmCalculado={kmCalculado}
                />
                <MemoizedDriverScheduleTable
                    params={params}
                    fetchDriversData={fetchDriversData}
                    driversData={driversData}
                    loading={loading}
                    zona={zona}
                    selectedFilterType={selectedFilterType}
                    selectedColumns={selectedColumns}
                    setKmCalculado={setKmCalculado}
                    kmCalculado={kmCalculado}
                />
            </div>
        </>
    );
};

export default MaestroDeServicios;

